import React, { useCallback, useState } from "react";
import { useTranslation, useMobileMode } from "Shared/util/custom-hooks";
import { AuthenticationFlowResource, ConsentResource } from "Shared/util/types";
import FadeTransition from "Shared/components/transitions/fade-transition";
import Button from "Shared/components/button";
import { OpenBankingPrivacyPolicy } from "../components";
import { useConsentCreater } from "../hooks";
import styles from "./select-bank-id-method-tab.css";
import { SelectBankIdMethodTabProps } from "./types";

export const SelectBankIdMethodTab = ({
    data: { affiliatedBankId, bic, finaBankId, socialSecurityNumber },
    onBankIdMethodSelected,
    onError,
    show,
}: SelectBankIdMethodTabProps) => {
    const isMobileMode = useMobileMode();
    const t = useTranslation((x) => x.edit_payment_information);
    const { select_bank_id_method_tab: ttab } = t;

    const [otherDeviceLoading, setOtherDeviceLoading] = useState(false);
    const [sameDeviceLoading, setSameDeviceLoading] = useState(false);

    const handleConsentCreated = useCallback(
        (
            authenticationFlow: AuthenticationFlowResource,
            consent: ConsentResource,
        ) => onBankIdMethodSelected(authenticationFlow, consent),
        [onBankIdMethodSelected],
    );

    const handleConsentError = useCallback(() => onError(true), [onError]);

    const handleConsentFinally = useCallback(() => {
        setOtherDeviceLoading(false);
        setSameDeviceLoading(false);
    }, []);

    const consentCreater = useConsentCreater(
        handleConsentCreated,
        handleConsentError,
        handleConsentFinally,
    );

    const handleUseOtherDevice = () => {
        setOtherDeviceLoading(true);
        consentCreater(
            AuthenticationFlowResource.DecoupledOtherDevice,
            finaBankId,
            bic,
            socialSecurityNumber,
            affiliatedBankId,
        );
    };

    const handleUseSameDevice = () => {
        setSameDeviceLoading(true);
        consentCreater(
            AuthenticationFlowResource.DecoupledSameDevice,
            finaBankId,
            bic,
            socialSecurityNumber,
            affiliatedBankId,
        );
    };

    const buttons = [
        <Button
            attributes={{
                disabled: sameDeviceLoading,
            }}
            buttonType={isMobileMode ? "action-secondary" : "action-primary"}
            isLoading={otherDeviceLoading}
            key="otherDevice"
            onClick={handleUseOtherDevice}
            text={ttab.bankid_on_another_device}
        />,
        <Button
            attributes={{
                disabled: otherDeviceLoading,
            }}
            buttonType={isMobileMode ? "action-primary" : "action-secondary"}
            isLoading={sameDeviceLoading}
            key="sameDevice"
            onClick={handleUseSameDevice}
            text={ttab.open_bankid}
        />,
    ];

    return (
        <FadeTransition
            className={styles.container}
            enterDuration={200}
            isOpen={show}
        >
            {!show ? null : (
                <>
                    <h3>{t.identify_with_bankid}</h3>
                    <OpenBankingPrivacyPolicy />
                    <div className={styles.bankIdButtonsContainer}>
                        {isMobileMode ? buttons.reverse() : buttons}
                    </div>
                </>
            )}
        </FadeTransition>
    );
};
